import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, XCircle } from "lucide-react";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 20; // ~40s at 2s interval
const INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | pending | error
  const pollsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    let timer;

    const poll = async () => {
      if (!active) return;
      if (pollsRef.current >= MAX_POLLS) {
        setState("pending");
        return;
      }
      pollsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        if (!active) return;
        // A 404 means the session isn't ours / doesn't exist — definitive, stop.
        if (e?.response?.status === 404) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="payment-success-page">
      <SEO page="/thank-you" title="Payment · The Modern Exit" path="/payment/success" />
      <Nav />
      <main className="pt-28 sm:pt-32 pb-20 px-5 sm:px-6 md:px-10">
        <div className="max-w-2xl mx-auto">
          {state === "polling" && (
            <div data-testid="payment-status-polling" className="border border-sand-300 bg-sand-50 p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay-deep">
                Confirming payment
              </p>
              <h1 className="font-display mt-4 text-3xl sm:text-4xl tracking-tight text-ink flex items-center gap-3">
                <Loader2 className="animate-spin text-clay" size={28} /> Verifying…
              </h1>
              <p className="mt-4 text-ink-muted leading-relaxed">
                Hold tight — we're confirming your payment with Stripe. This
                usually takes a few seconds.
              </p>
            </div>
          )}

          {state === "paid" && (
            <div data-testid="payment-status-paid" className="border border-olive/40 bg-olive/5 p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-olive flex items-center gap-2">
                <CheckCircle2 size={13} /> Payment confirmed
              </p>
              <h1 className="font-display mt-4 text-3xl sm:text-4xl tracking-tight text-ink">
                You're in. Payment received.
              </h1>
              <p className="mt-4 text-ink-muted leading-relaxed max-w-xl">
                Thank you — your payment cleared. The Modern Exit will reach out
                with your onboarding and next steps shortly.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/book" data-testid="payment-book-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-ink transition-colors min-h-[48px]">
                  Book your kickoff call <ArrowRight size={15} />
                </Link>
                <Link to="/" className="inline-flex items-center justify-center gap-2 px-4 py-3.5 text-[14px] text-ink hover:text-clay transition-colors">
                  Back to home
                </Link>
              </div>
            </div>
          )}

          {state === "pending" && (
            <div data-testid="payment-status-pending" className="border border-clay/40 bg-sand-50 p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-clay-deep">
                Still processing
              </p>
              <h1 className="font-display mt-4 text-3xl sm:text-4xl tracking-tight text-ink">
                Your payment is still processing.
              </h1>
              <p className="mt-4 text-ink-muted leading-relaxed max-w-xl">
                This is taking a little longer than usual. If you completed
                checkout, you'll receive a confirmation shortly — no need to pay
                again. Contact us if anything looks off.
              </p>
              <Link to="/" className="mt-7 inline-flex items-center gap-2 text-[13px] text-ink-muted hover:text-clay transition-colors">
                <ArrowLeft size={13} /> Back to home
              </Link>
            </div>
          )}

          {state === "error" && (
            <div data-testid="payment-status-error" className="border border-rust/40 bg-rust/5 p-8 sm:p-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-rust flex items-center gap-2">
                <XCircle size={13} /> Something went wrong
              </p>
              <h1 className="font-display mt-4 text-3xl sm:text-4xl tracking-tight text-ink">
                We couldn't confirm this payment.
              </h1>
              <p className="mt-4 text-ink-muted leading-relaxed max-w-xl">
                We couldn't find or confirm this checkout session. If you were
                charged, don't worry — reach out and we'll sort it immediately.
              </p>
              <Link to="/pricing" className="mt-7 inline-flex items-center gap-2 rounded-full bg-ink-deep text-sand-100 px-6 py-3.5 text-[14px] font-medium hover:bg-ink transition-colors min-h-[48px]">
                Back to pricing <ArrowRight size={15} />
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
